import type { CardDetailResponse, CardListQuery, ListingQuery } from "@pokifolio/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApi } from "../../app/api-context";

export const CARDS_KEY = ["cards"] as const;
export const NOTIFICATIONS_KEY = ["notifications"] as const;

export function useCardList(query: CardListQuery) {
  const api = useApi();
  return useQuery({
    queryKey: [...CARDS_KEY, "list", query],
    queryFn: () => api.listCards(query),
    placeholderData: keepPreviousData,
  });
}

export function useCard(id: string) {
  const api = useApi();
  return useQuery({ queryKey: [...CARDS_KEY, "card", id], queryFn: () => api.getCard(id) });
}

export function useLinkRequests(enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...CARDS_KEY, "requests"],
    queryFn: () => api.linkRequests(),
    enabled,
  });
}

/** Any change to a card: the page shows the answer at once; lists, requests and the bell refresh. */
export function useCardChange<TArgs>(id: string, send: (args: TArgs) => Promise<CardDetailResponse>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: async (response) => {
      queryClient.setQueryData([...CARDS_KEY, "card", id], response);
      await queryClient.invalidateQueries({ queryKey: CARDS_KEY });
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/** The bell: checked every 30 seconds. */
export function useNotifications() {
  const api = useApi();
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => api.notifications(),
    refetchInterval: 30_000,
  });
}

export function useListings(query: ListingQuery) {
  const api = useApi();
  return useQuery({
    queryKey: [...CARDS_KEY, "listings", query],
    queryFn: () => api.listListings(query),
    placeholderData: keepPreviousData,
  });
}

export function useCardStats(scope: "all" | "mine") {
  const api = useApi();
  return useQuery({ queryKey: [...CARDS_KEY, "stats", scope], queryFn: () => api.cardStats(scope) });
}
